import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'

const tokens = {
  dark: {
    bgInput: 'bg.input',
    bgPopover: 'bg.popover',
    bgDayHover: 'bg.table-row-hover',
    bgDaySelected: 'bg.calendar-selected',
    textPrimary: 'text.primary',
    textTertiary: 'text.tertiary',
    textDisabled: 'text.disabled',
    textDaySelected: 'text.calendar-selected',
    // Matches the other trigger fields (Select, MultiSelectInput) — see
    // MultiSelectInput's placeholderStyles.
    placeholder: 'text.primary',
    label: 'text.secondary',
    border: 'border.input',
    borderSubtle: 'border.subtle',
    borderHover: 'border.input-hover',
    borderFocus: 'border.input-focus',
    /** CSS var — painted via box-shadow ring, which can't take a token name. */
    borderToday: tokenVar('colors.border.calendar-today'),
    iconSecondary: 'icon.secondary',
  },
  light: {
    bgInput: 'light.bg.input',
    // NOT light.bg.popover — the Legacy token keeps popover surfaces dark
    // (tooltips), but a calendar in the light theme must be light.
    bgPopover: 'light.bg.card',
    bgDayHover: 'light.bg.table-row-hover',
    bgDaySelected: 'light.bg.calendar-selected',
    textPrimary: 'light.text.primary',
    textTertiary: 'light.text.tertiary',
    textDisabled: 'light.text.disabled',
    textDaySelected: 'light.text.calendar-selected',
    placeholder: 'light.text.primary',
    label: 'light.text.secondary',
    border: 'light.border.input',
    borderSubtle: 'light.border.subtle',
    borderHover: 'light.border.input-hover',
    borderFocus: 'light.border.input-focus',
    borderToday: tokenVar('colors.light.border.calendar-today'),
    iconSecondary: 'light.icon.secondary',
  },
}

/**
 * Eyebrow label above the input — the Selection field subtitle slot
 * (Figma "Detail": Söhne Kräftig 10/16, 1px tracking, uppercase,
 * text/secondary). Matches MultiSelectInput's inputLabelStyles so labelled
 * fields inside one panel read identically.
 */
export const datePickerLabelStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontSize: '10px',
  fontWeight: '500',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  lineHeight: '16px',
  mb: '4px',
  color: tokens[colorScheme].label,
  userSelect: 'none',
})

/**
 * Styles the react-datepicker input and inline popper through descendant
 * selectors, since the library renders plain classed markup rather than
 * accepting style props. The popper is not portaled, so everything stays
 * inside this wrapper.
 *
 * Bindings follow the design library:
 * - Input: Selection field — bg/input, border/input → -hover → -focus,
 *   value text/primary, placeholder text/secondary, Small/Body (11/16).
 * - Calendar: bg/popover + border/subtle surface, Body Bold month title,
 *   Caption Medium text/tertiary weekday row, circular day cells,
 *   selected = bg/calendar-selected + text/calendar-selected,
 *   today = border/calendar-today ring, nav arrows icon/secondary.
 */
export const datePickerWrapperStyles = (
  colorScheme: ColorScheme,
  isDisabled: boolean,
  inputBorderColor?: string
): SystemStyleObject => {
  const t = tokens[colorScheme]
  return {
    width: '100%',
    opacity: isDisabled ? 0.5 : 1,
    '& .react-datepicker-wrapper': {
      width: '100%',
    },
    '& .react-datepicker__input-container input': {
      bg: t.bgInput,
      border: '1px solid',
      borderColor: inputBorderColor ?? t.border,
      borderRadius: '0px',
      color: t.textPrimary,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      fontFamily: 'Soehne',
      fontSize: '11px',
      lineHeight: '16px',
      height: '36px',
      outline: 'none',
      px: '10px',
      transition: 'border-color 0.15s ease',
      width: '100%',
      _hover: isDisabled ? {} : { borderColor: t.borderHover },
      _focus: { borderColor: t.borderFocus },
      _placeholder: { color: t.placeholder },
    },
    // The default triangle is styled for the library's light theme; the flat
    // panel reads cleaner without it.
    '& .react-datepicker__triangle': {
      display: 'none',
    },
    '& .react-datepicker': {
      bg: t.bgPopover,
      border: '1px solid',
      borderColor: t.borderSubtle,
      borderRadius: '0px',
      boxShadow: 'dropdown',
      color: t.textPrimary,
      fontFamily: 'Soehne',
    },
    // Header sits on the popover surface — no separate fill per the spec.
    '& .react-datepicker__header': {
      bg: 'transparent',
      borderBottom: '1px solid',
      borderBottomColor: t.borderSubtle,
      borderRadius: '0px',
    },
    // Month/year title: Body Bold.
    '& .react-datepicker__current-month': {
      color: t.textPrimary,
      fontFamily: "'Soehne Kraftig', sans-serif",
      fontSize: '16px',
      fontWeight: '500',
      lineHeight: '24px',
    },
    // ‹ › month-nav chevrons.
    '& .react-datepicker__navigation-icon::before': {
      borderColor: t.iconSecondary,
    },
    // Weekday row: Caption Medium.
    '& .react-datepicker__day-name': {
      color: t.textTertiary,
      fontFamily: "'Soehne Kraftig', sans-serif",
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '16px',
    },
    // Day cells: circular highlight on a tight gutter grid.
    '& .react-datepicker__day': {
      borderRadius: 'full',
      color: t.textPrimary,
      _hover: { bg: t.bgDayHover, borderRadius: 'full' },
    },
    '& .react-datepicker__day--outside-month': {
      color: t.textTertiary,
    },
    '& .react-datepicker__day--disabled': {
      color: t.textDisabled,
      _hover: { bg: 'transparent' },
    },
    '& .react-datepicker__day--keyboard-selected': {
      bg: t.bgDayHover,
      borderRadius: 'full',
    },
    // Today (when not the selected day): dawn-blue ring, no fill. Painted as
    // an inset shadow so the cell box doesn't grow; the selected style below
    // clears it.
    '& .react-datepicker__day--today': {
      boxShadow: `inset 0 0 0 1px ${t.borderToday}`,
      fontWeight: 'inherit',
    },
    '& .react-datepicker__day--selected': {
      bg: t.bgDaySelected,
      borderRadius: 'full',
      boxShadow: 'none',
      color: t.textDaySelected,
      _hover: { bg: t.bgDaySelected },
    },
    // ─── Time column (showTimeSelect / showTimeSelectOnly) ──────────────────
    // The library renders the time list with its own light theme; these
    // bindings match the calendar surface above so a time-only picker reads
    // the same in both modes.
    '& .react-datepicker__time-container': {
      borderLeft: '1px solid',
      borderLeftColor: t.borderSubtle,
    },
    '& .react-datepicker__time, & .react-datepicker__time-box': {
      bg: t.bgPopover,
    },
    // Time-only mode drops the calendar, so the time box spans the panel.
    '& .react-datepicker--time-only .react-datepicker__time-box': {
      width: '100%',
    },
    // "Time" caption: Body Bold, matching the calendar's month title.
    '& .react-datepicker-time__header': {
      color: t.textPrimary,
      fontFamily: "'Soehne Kraftig', sans-serif",
      fontSize: '16px',
      fontWeight: '500',
      lineHeight: '24px',
    },
    '& .react-datepicker__time-list-item': {
      color: t.textPrimary,
      fontFamily: 'Soehne',
      fontSize: '11px',
      // react-datepicker themes the time list through a long, high-specificity
      // descendant chain, so hover/selected must be emitted as a CSS var with
      // !important to win. Values match the calendar day cells: neutral hover,
      // accent for selection.
      _hover: { bg: `${tokenVar(`colors.${t.bgDayHover}`)} !important` },
    },
    '& .react-datepicker__time-list-item--selected': {
      bg: `${tokenVar(`colors.${t.bgDaySelected}`)} !important`,
      color: `${tokenVar(`colors.${t.textDaySelected}`)} !important`,
      fontWeight: 'inherit',
      _hover: { bg: `${tokenVar(`colors.${t.bgDaySelected}`)} !important` },
    },
    '& .react-datepicker__time-list-item--disabled': {
      color: t.textDisabled,
      _hover: { bg: 'transparent' },
    },
  }
}
